import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/AlarmProfiles/AssignmentDialog.jsx
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Checkbox,
    Alert,
    CircularProgress,
    IconButton,
    Chip
} from '@mui/material';
import { Close as CloseIcon, Assignment as AssignIcon } from '@mui/icons-material';

const AssignmentDialog = ({
    open,
    onClose,
    profile,
    vehicles,
    compatibleVehicles,
    selectedVehicles,
    setSelectedVehicles,
    loading,
    onAssign,
    assigning,
    getProfileAssignments,
    getVehicleDisplayName
}) => {
  const { t } = useTranslation();
    if (!profile) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h6">{t('hardcoded.components_alarmprofiles_assignmentdialog.asignar_perfil')}{profile.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.components_alarmprofiles_assignmentdialog.vehiculos_compatibles')}{compatibleVehicles.length} disponibles)
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Requisito:</strong> {t('hardcoded.components_alarmprofiles_assignmentdialog.este_perfil_requiere_vehiculos_con')}{profile.axle_count} {i18n.t('hardcoded.components_alarmprofiles_assignmentdialog.ejes')}</Typography>
                    </Alert>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>{i18n.t('common.loading')}</Typography>
                    </Box>
                ) : compatibleVehicles.length === 0 ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            <strong>{t('hardcoded.components_alarmprofiles_assignmentdialog.no_hay_vehiculos_compatibles')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {t('hardcoded.components_alarmprofiles_assignmentdialog.no_se_encontraron_vehiculos_con')}{profile.axle_count} {i18n.t('hardcoded.components_alarmprofiles_assignmentdialog.ejes')}</Typography>
                    </Alert>
                ) : (
                    <Grid container spacing={2}>
                        {compatibleVehicles.map((vehicle) => {
                            const isSelected = selectedVehicles.includes(vehicle.id);
                            const assignedProfile = getProfileAssignments(profile.id).find(
                                (a) => String(a.vehicle_id) === String(vehicle.id)
                            );

                            return (
                                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                                    <Card
                                        sx={{
                                            cursor: assignedProfile ? 'not-allowed' : 'pointer',
                                            border: isSelected ? 2 : 1,
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            bgcolor: isSelected
                                                ? 'primary.50'
                                                : assignedProfile
                                                    ? 'grey.100'
                                                    : 'white',
                                            '&:hover': !assignedProfile
                                                ? { borderColor: 'primary.main', boxShadow: 2 }
                                                : {}
                                        }}
                                        onClick={() => {
                                            if (!assignedProfile) {
                                                if (isSelected)
                                                    setSelectedVehicles((prev) =>
                                                        prev.filter((id) => id !== vehicle.id)
                                                    );
                                                else setSelectedVehicles((prev) => [...prev, vehicle.id]);
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={assignedProfile}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        if (e.target.checked)
                                                            setSelectedVehicles((prev) => [...prev, vehicle.id]);
                                                        else
                                                            setSelectedVehicles((prev) =>
                                                                prev.filter((id) => id !== vehicle.id)
                                                            );
                                                    }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                        {getVehicleDisplayName(vehicle)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        {vehicle.make} {vehicle.model}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Chip
                                                            label={t('hardcoded.components_alarmprofiles_assignmentdialog.axle_count_ejes', { axle_count: vehicle.axle_count })}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                        {assignedProfile ? (
                                                            <Chip label={t('gps.card.assigned')} size="small" color="success" />
                                                        ) : (
                                                            <Chip
                                                                label={t('hardcoded.components_alarmprofiles_assignmentdialog.disponible')}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} size="large">
                    {t('common.cancel')}</Button>
                <Button
                    onClick={onAssign}
                    variant="contained"
                    size="large"
                    disabled={selectedVehicles.length === 0 || assigning}
                    startIcon={assigning ? <CircularProgress size={20} /> : <AssignIcon />}
                >
                    {assigning
                        ? t('hardcoded.components_alarmprofiles_assignmentdialog.asignando')
                        : t('hardcoded.components_alarmprofiles_assignmentdialog.asignar_a_length_vehiculoselectedvehicle', { length: selectedVehicles.length, selectedVehicle: selectedVehicles.length !== 1 ? 's' : '' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignmentDialog;